import dayjs from '@/lib/dayjs'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import type {
  CashDrawerReportSession,
  CashDrawerWorkstation,
} from '@/lib/services/cash-drawers/cash-drawers.types'

type DifferenceAmount = number | string | null | undefined
interface ReportSessionTiming {
  id: number | string
  closed_at: string | null
}
interface ClosingDifferences {
  closing_cash_discrepancy?: DifferenceAmount
  closing_check_discrepancy?: DifferenceAmount
}

export function getCashDrawerReportSessions<T extends ReportSessionTiming>(
  sessions: T[],
  filters: { start_date: string; end_date: string },
  timezone: string
) {
  return sessions
    .filter((session) => {
      if (!session.closed_at) return true
      const date = dayjs(session.closed_at).tz(timezone).format('YYYY-MM-DD')
      return date >= filters.start_date && date <= filters.end_date
    })
    .toSorted((left, right) => {
      if (!left.closed_at) return right.closed_at ? 1 : Number(right.id) - Number(left.id)
      if (!right.closed_at) return -1
      return (
        dayjs(right.closed_at).valueOf() - dayjs(left.closed_at).valueOf() ||
        Number(right.id) - Number(left.id)
      )
    })
}

export function getCloseBalance(session: ClosingDifferences) {
  const cash = session.closing_cash_discrepancy
  const checks = session.closing_check_discrepancy
  if (cash == null || checks == null) return 'unknown'
  const balanced =
    Math.round(parseCurrency(cash) * 100) === 0 && Math.round(parseCurrency(checks) * 100) === 0
  return balanced ? 'balanced' : 'different'
}

/** A closed count as a chart bar, its over/short amounts parsed to numbers for Recharts. */
export type CloseChartRow<T extends ReportSessionTiming> = T & {
  closed_at: string
  cash: number | null
  checks: number | null
}

export function buildCloseChartData<T extends ReportSessionTiming & ClosingDifferences>(
  sessions: T[]
): CloseChartRow<T>[] {
  return sessions
    .filter((session): session is T & { closed_at: string } => Boolean(session.closed_at))
    .toReversed()
    .map((session) => ({
      ...session,
      cash:
        session.closing_cash_discrepancy == null
          ? null
          : parseCurrency(session.closing_cash_discrepancy),
      checks:
        session.closing_check_discrepancy == null
          ? null
          : parseCurrency(session.closing_check_discrepancy),
    }))
}

export function formatCashChartDate(value: string, timezone: string) {
  return dayjs(value).tz(timezone).format('M/D')
}

export function formatCashDrawerDifference(value: DifferenceAmount) {
  if (value == null) return '—'
  const amount = parseCurrency(value)
  return `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`
}

export function cashDrawerDifferenceTone(value: DifferenceAmount) {
  if (value == null) return 'muted'
  const amount = parseCurrency(value)
  return amount < 0 ? 'destructive' : amount > 0 ? 'warning' : 'success'
}

// Balancing only offers active registers; a historical link must never fall back to another one.
export function getReportBalancingSearch(
  session: Pick<CashDrawerReportSession, 'id' | 'workstation_id'>,
  workstations: CashDrawerWorkstation[],
  canBalance: boolean
): { workstation_id: number; session_id: number } | undefined {
  if (
    !canBalance ||
    !workstations.some(
      (workstation) =>
        workstation.id === session.workstation_id &&
        workstation.is_active &&
        workstation.location.is_active
    )
  )
    return undefined
  return { workstation_id: session.workstation_id, session_id: session.id }
}
